import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
} from '@ludolio/shared-ui';
import { BookOpen, Plus, Trash2 } from 'lucide-react';
import {
  formatAgeRating,
  getAgeRatingDescriptors,
  getRegionAwareAgeRating,
} from '@ludolio/shared-ui/utils/age-rating';
import { useUnitsModule } from '../../module-context';
import type { Game } from '../../types';

interface GamesTabProps {
  unitId: string;
  games: Game[];
  unitRegion?: string | null;
  onDataChanged(): Promise<void>;
}

function getRating(game: Game, region?: string | null) {
  return formatAgeRating(getRegionAwareAgeRating(game.ageRatings, region));
}

function getDescriptors(game: Game, region?: string | null) {
  return getAgeRatingDescriptors(getRegionAwareAgeRating(game.ageRatings, region));
}

export function GamesTab({ unitId, games, unitRegion, onDataChanged }: GamesTabProps) {
  const { api, capabilities, reportError } = useUnitsModule();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [availableGames, setAvailableGames] = useState<Game[]>([]);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [gameToRemove, setGameToRemove] = useState<string | null>(null);

  const loadAvailableGames = async () => {
    if (!capabilities.canManageGames) return;
    try {
      const response = await api.getAllGames();
      if (response.success && response.data) setAvailableGames(response.data);
    } catch (error) {
      console.error('Failed to load games:', error);
      reportError(error);
    }
  };

  const handleAddGame = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedGameId || !capabilities.canManageGames) return;
    try {
      await api.addGameToUnit(unitId, selectedGameId);
      setAddDialogOpen(false);
      setSelectedGameId('');
      await onDataChanged();
    } catch (error) {
      console.error('Failed to add game:', error);
      reportError(error);
    }
  };

  const handleRemoveGame = async () => {
    if (!gameToRemove || !capabilities.canManageGames) return;
    try {
      await api.removeGameFromUnit(unitId, gameToRemove);
      setGameToRemove(null);
      await onDataChanged();
    } catch (error) {
      console.error('Failed to remove game:', error);
      reportError(error);
    }
  };

  const openAddDialog = () => {
    if (!capabilities.canManageGames) return;
    setAddDialogOpen(true);
    void loadAvailableGames();
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Assigned Games</CardTitle>
            {capabilities.canManageGames && (
              <Dialog
                open={addDialogOpen}
                onOpenChange={(open) => {
                  setAddDialogOpen(open);
                  if (open) void loadAvailableGames();
                }}
              >
                <DialogTrigger asChild>
                  <Button><Plus className="mr-2 h-4 w-4" />Add Game</Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] w-[95vw] max-w-2xl overflow-x-hidden overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Game to Unit</DialogTitle>
                    <DialogDescription>Select a game to assign to this unit</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddGame} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="game">Game</Label>
                      <select
                        id="game"
                        value={selectedGameId}
                        onChange={(event) => setSelectedGameId(event.target.value)}
                        className="w-full rounded-md border px-3 py-2"
                        required
                      >
                        <option value="">Select a game...</option>
                        {availableGames
                          .filter((game) => !games.some((assignedGame) => assignedGame.id === game.id))
                          .map((game) => <option key={game.id} value={game.id}>{game.title}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" disabled={!selectedGameId}>Add Game</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {games.length === 0 ? (
            <div className="py-12 text-center">
              <BookOpen className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">No games assigned</h3>
              <p className="mb-4 text-muted-foreground">
                {capabilities.canManageGames
                  ? 'Add games to this unit to get started.'
                  : 'No games are currently assigned to this unit.'}
              </p>
              {capabilities.canManageGames && (
                <Button onClick={openAddDialog}><Plus className="mr-2 h-4 w-4" />Add Your First Game</Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {games.map((game) => {
                const rating = getRating(game, unitRegion);
                const descriptors = getDescriptors(game, unitRegion);
                return (
                  <Card key={game.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <CardTitle className="text-base">{game.title}</CardTitle>
                            {rating && (
                              <Badge variant="outline" className="shrink-0 border-amber-300/60 bg-amber-50 text-[10px] text-amber-800">
                                {rating}
                              </Badge>
                            )}
                          </div>
                          <CardDescription className="mt-1 line-clamp-2">{game.description}</CardDescription>
                          {descriptors.length > 0 && (
                            <p className="mt-2 text-xs text-muted-foreground">Rating notes: {descriptors.join(', ')}</p>
                          )}
                        </div>
                        {capabilities.canManageGames && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setGameToRemove(game.id)}
                            className="text-destructive hover:text-destructive"
                            aria-label={`Remove ${game.title}`}
                            title={`Remove ${game.title}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Version</span>
                          <Badge variant="outline">{game.version}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Status</span>
                          <Badge variant={game.status === 'published' ? 'default' : 'secondary'}>{game.status}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={gameToRemove !== null} onOpenChange={(open) => !open && setGameToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Game</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this game from the unit? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setGameToRemove(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleRemoveGame()}>Remove Game</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
